import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { timeframeDateRange } from '@/components/ReviewsManagement/Reviews/timeframeDateRange'
import { TimeFrameOptionsEnum } from '@/components/ReviewsManagement/Reviews/types'
import type { RecommendationRatingKeyEnum, UniversalReviewStates } from '@/gql/reviews'

import { agencyReviewsFiltersFromQuery } from './agencyReviewsFiltersFromQuery'

export const useRouterQueryAgencyReviewsFilters = () => {
  const route = useRoute()
  const router = useRouter()

  const filters = computed(() => agencyReviewsFiltersFromQuery(route.query))
  const timeframe = computed(() => filters.value.timeframe)
  const ratings = computed(() => filters.value.ratings)
  const states = computed(() => filters.value.states)
  const dateRange = computed(() => timeframeDateRange(filters.value.timeframe))
  const hasActiveFilters = computed(
    () => filters.value.timeframe !== TimeFrameOptionsEnum.NONE || filters.value.ratings.length > 0 || filters.value.states.length > 0,
  )

  function pushFilters(next: {
    timeframe?: TimeFrameOptionsEnum
    ratings?: RecommendationRatingKeyEnum[]
    states?: UniversalReviewStates[]
  }) {
    const timeframeValue = next.timeframe ?? filters.value.timeframe
    const ratingsValue = next.ratings ?? filters.value.ratings
    const statesValue = next.states ?? filters.value.states
    router.push({
      query: {
        ...route.query,
        timeframe: timeframeValue !== TimeFrameOptionsEnum.NONE ? timeframeValue : undefined,
        rating: ratingsValue.length ? ratingsValue.join(',') : undefined,
        state: statesValue.length ? statesValue.join(',') : undefined,
        page: undefined,
      },
    })
  }

  function setTimeframe(value: TimeFrameOptionsEnum) {
    pushFilters({ timeframe: value })
  }

  function setRatings(value: RecommendationRatingKeyEnum[]) {
    pushFilters({ ratings: value })
  }

  function setStates(value: UniversalReviewStates[]) {
    pushFilters({ states: value })
  }

  function resetFilters() {
    router.push({ query: { ...route.query, timeframe: undefined, rating: undefined, state: undefined, page: undefined } })
  }

  return {
    timeframe,
    ratings,
    states,
    dateRange,
    hasActiveFilters,
    setTimeframe,
    setRatings,
    setStates,
    resetFilters,
  }
}
